import { FormEvent, useState } from 'react';
import { indusvivaApi, type LiveLocation } from '../../lib/indusvivaApi';
import { useStore } from '../../store/StoreProvider';

/**
 * Mandatory on first visit: user enters pincode → live delivery check via Indusviva API.
 */
export function PincodeGate() {
  const { showPincodeModal, selectPincode, pincodeReady, setShowPincodeModal, pincode } = useStore();
  const [pin, setPin] = useState(pincode || '');
  const [options, setOptions] = useState<LiveLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!showPincodeModal) return null;

  const onCheck = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(pin)) {
      setError('Enter a valid 6-digit Indian pincode');
      return;
    }
    setLoading(true);
    try {
      const locs = await indusvivaApi.locations(pin);
      const serviceable = locs.filter((l) => Number(l.is_service_available) === 1);
      if (!locs.length) {
        setError('No delivery locations found for this pincode');
        setOptions([]);
        return;
      }
      if (!serviceable.length) {
        setError('Delivery is not available for this pincode');
        setOptions(locs);
        return;
      }
      setOptions(serviceable);
      if (serviceable.length === 1) {
        await selectPincode(pin, serviceable[0], serviceable);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to check pincode');
    } finally {
      setLoading(false);
    }
  };

  const canClose = pincodeReady;

  return (
    <div className="ileafs-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="pincode-title">
      <div className="ileafs-modal">
        <div className="ileafs-modal__badge">Indus Viva Verified Reseller</div>
        <h2 id="pincode-title">Check delivery availability</h2>
        <p>Enter your pincode to load live Indus Viva products available for delivery in your area.</p>

        <form onSubmit={onCheck} className="ileafs-modal__form">
          <label htmlFor="pincode-input">Pincode</label>
          <input
            id="pincode-input"
            inputMode="numeric"
            maxLength={6}
            placeholder="e.g. 560001"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoFocus
          />
          <button type="submit" className="vs-btn style2" disabled={loading}>
            {loading ? 'Checking…' : 'Check availability'}
          </button>
        </form>

        {error ? <p className="ileafs-modal__error">{error}</p> : null}

        {options.length > 1 ? (
          <div className="ileafs-modal__list">
            <p>Select your post office:</p>
            <ul>
              {options.map((loc) => (
                <li key={loc.postalcode_id}>
                  <button
                    type="button"
                    className="ileafs-modal__option"
                    onClick={() => void selectPincode(pin, loc, options)}
                  >
                    <strong>{loc.post_name}</strong>
                    <span>
                      {loc.city_name}, {loc.state_name}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {canClose ? (
          <button type="button" className="ileafs-modal__link" onClick={() => setShowPincodeModal(false)}>
            Continue shopping
          </button>
        ) : null}
      </div>
    </div>
  );
}
